import React, { useRef, useState, useEffect } from "react";

const SPEEDS = [0.25, 0.5, 1];

export default function VideoPlayer({ src, issues = [], onReady }) {
  const ref = useRef(null);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [speed, setSpeed] = useState(1);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const onMeta = () => setDuration(v.duration || 0);
    const onTime = () => setCurrent(v.currentTime || 0);
    v.addEventListener("loadedmetadata", onMeta);
    v.addEventListener("timeupdate", onTime);
    if (onReady) {
      onReady({
        jumpTo: (t) => { v.currentTime = Math.max(0, Math.min(v.duration || 0, t)); v.play?.().catch(() => {}); },
        setRate: (r) => { v.playbackRate = r; setSpeed(r); },
      });
    }
    return () => { v.removeEventListener("loadedmetadata", onMeta); v.removeEventListener("timeupdate", onTime); };
  }, [src, onReady]);

  const setPlaybackRate = (r) => {
    setSpeed(r);
    if (ref.current) ref.current.playbackRate = r;
  };

  const jumpTo = (t) => {
    if (!ref.current) return;
    ref.current.currentTime = Math.max(0, Math.min(duration || 0, t));
    ref.current.play?.().catch(() => {});
  };

  // filter to markers that actually have a numeric timestamp
  const markers = (issues || []).filter((i) => typeof i.timestamp_seconds === "number" && i.timestamp_seconds >= 0);

  return (
    <div data-testid="video-player" className="border border-neutral-800 rounded-md bg-black overflow-hidden">
      <video ref={ref} src={src} controls playsInline className="w-full aspect-video block" />
      {duration > 0 && (
        <div className="relative h-6 bg-neutral-950 border-t border-neutral-800">
          {/* elapsed bar */}
          <div className="absolute top-0 left-0 h-full bg-neutral-800" style={{ width: `${(current / duration) * 100}%` }} />
          {/* markers */}
          {markers.map((m, i) => {
            const pct = (m.timestamp_seconds / duration) * 100;
            return (
              <button
                key={m.id || i}
                data-testid={`timeline-marker-${i}`}
                onClick={() => jumpTo(m.timestamp_seconds)}
                title={`${m.title} @ ${m.timestamp_seconds.toFixed(1)}s`}
                className="absolute top-0 h-full w-1.5 -ml-[3px] bg-red-500 hover:bg-red-400 hover:w-2 hover:-ml-[4px] transition-all"
                style={{ left: `${pct}%` }}
              />
            );
          })}
        </div>
      )}
      <div className="flex items-center justify-between gap-3 px-3 py-2 bg-neutral-950 border-t border-neutral-800">
        <div className="text-xs text-neutral-500 uppercase font-display tracking-widest">Speed</div>
        <div className="flex gap-1" data-testid="speed-toggle">
          {SPEEDS.map((r) => (
            <button
              key={r}
              data-testid={`speed-${r}`}
              onClick={() => setPlaybackRate(r)}
              className={`h-8 px-3 rounded-md text-sm font-display font-semibold transition-colors ${speed === r ? "bg-red-600 text-white" : "bg-neutral-900 text-neutral-300 hover:bg-neutral-800"}`}>
              {r}×
            </button>
          ))}
        </div>
        <div className="text-xs text-neutral-500 hidden sm:block">
          {markers.length > 0 ? `${markers.length} moment${markers.length === 1 ? "" : "s"} flagged — tap a red marker to jump` : "No timeline markers"}
        </div>
      </div>
    </div>
  );
}
